import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from './components/layout/AppShell';
import { initializeTheme } from './lib/theme';
import cities from './data/cities';
import CalendarPreferences from './components/calendar/CalendarPreferences';
import GoogleCalendarConnect from './components/calendar/GoogleCalendarConnect';
import { useUserPreferences } from './hooks/useUserPreferences';
import { useGoogleCalendar } from './hooks/useGoogleCalendar';
import { useAuth } from './hooks/useAuth';
import { useAuthenticatedServices } from './hooks/useAuthenticatedServices';
import CalendarSignInModal from './components/calendar/CalendarSignInModal';
import AuthWrapper from './components/auth/AuthWrapper';
import HelpModal from './components/shared/HelpModal';
import App from './App';

const TimezoneGroups = React.lazy(() =>
  import('./components/timezone-groups/TimezoneGroups').then(m => ({ default: m.TimezoneGroups }))
);

export default function AppWithShell() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  useAuthenticatedServices();
  const { refreshStatus: refreshCalendarStatus } = useGoogleCalendar();
  const {
    selectedTimezones,
    timeFormat,
    setTimeFormat,
    addTimezone,
    removeTimezone,
    reorderTimezones,
    loadPreset,
    addPreset,
    updateActiveGroupWithCurrentTimezones,
    getPresets,
    loading: preferencesLoading,
  } = useUserPreferences();

  const use24HourFormat = timeFormat === '24h';
  const setUse24HourFormat = useCallback((value) => {
    setTimeFormat(value ? '24h' : '12h');
  }, [setTimeFormat]);

  const selectedCities = useMemo(() => {
    return selectedTimezones.map(tz => {
      const city = cities.find(c => c.name === tz.name);
      return city || tz;
    }).filter(Boolean);
  }, [selectedTimezones]);

  const getLocalizedCityName = (city) => {
    const lng = i18n.language;
    if (lng?.startsWith('fr') && city.nameFr) return city.nameFr;
    if (lng?.startsWith('es') && city.nameEs) return city.nameEs;
    if (lng?.startsWith('de') && city.nameDe) return city.nameDe;
    if (lng?.startsWith('pt') && city.namePt) return city.namePt;
    if (lng?.startsWith('zh') && city.nameZh) return city.nameZh;
    return city.name;
  };

  const handleAddCity = async (city) => {
    if (!selectedCities.some(c => c.name === city.name)) {
      if (isAuthenticated) {
        await updateActiveGroupWithCurrentTimezones([...selectedTimezones, city]);
      } else {
        await addTimezone(city);
      }
    }
  };

  const handleRemoveCity = async (cityName) => {
    if (isAuthenticated) {
      const updated = selectedTimezones.filter(tz => tz.name !== cityName);
      await updateActiveGroupWithCurrentTimezones(updated);
    } else {
      await removeTimezone(cityName);
    }
  };

  const handleReorderCities = async (orderedNames) => {
    const lookup = new Map(selectedTimezones.map(tz => [tz.name, tz]));
    const next = orderedNames.map(n => lookup.get(n)).filter(Boolean);
    if (isAuthenticated) {
      await updateActiveGroupWithCurrentTimezones(next);
    } else {
      await reorderTimezones(next);
    }
  };

  const [showCalendarPreferences, setShowCalendarPreferences] = useState(false);
  const [showCalendarConnect, setShowCalendarConnect] = useState(false);
  const [showCalendarSignIn, setShowCalendarSignIn] = useState(false);
  const [showTimezoneGroups, setShowTimezoneGroups] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const [isOpeningPreferences, setIsOpeningPreferences] = useState(false);

  const handleToggleHelp = () => setShowHelp(v => !v);

  const handleOpenCalendarPreferences = async () => {
    if (!isAuthenticated) { setShowCalendarSignIn(true); return; }
    try {
      setIsOpeningPreferences(true);
      const status = await refreshCalendarStatus();
      if (status.connected) {
        setShowCalendarConnect(false);
        setShowCalendarPreferences(true);
      } else {
        setShowCalendarPreferences(false);
        setShowCalendarConnect(true);
      }
    } finally {
      setTimeout(() => setIsOpeningPreferences(false), 200);
    }
  };

  const handleOpenTimezoneGroups = () => setShowTimezoneGroups(true);

  const handleLoadGroup = useCallback(async (groupId) => {
    try {
      await loadPreset(groupId);
      setShowTimezoneGroups(false);
      window.dispatchEvent(new CustomEvent('activeGroupChanged', { detail: { activeGroupId: groupId } }));
    } catch (e) { console.error('Error loading group:', e); }
  }, [loadPreset]);

  const handleSaveCurrentGroup = async (name) => {
    try {
      const fullCityData = selectedTimezones.map(tz => cities.find(c => c.name === tz.name) || tz);
      await addPreset(name, fullCityData);
    } catch (e) { console.error('Error saving group:', e); }
  };

  useEffect(() => { initializeTheme(); }, []);

  useEffect(() => {
    const onKey = async (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key >= '1' && e.key <= '9') {
        e.preventDefault();
        if (isAuthenticated && !preferencesLoading) {
          try {
            const groups = await getPresets();
            const idx = parseInt(e.key, 10) - 1;
            if (groups[idx]) await handleLoadGroup(groups[idx].id);
          } catch (err) { console.error(err); }
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isAuthenticated, preferencesLoading, getPresets, handleLoadGroup]);

  const handleCalendarChange = async () => {
    const status = await refreshCalendarStatus();
    if (status.connected && showCalendarConnect) setShowCalendarConnect(false);
    else if (!status.connected && showCalendarPreferences) {
      setShowCalendarPreferences(false);
      if (!isOpeningPreferences) setShowCalendarConnect(true);
    }
    setCalendarRefreshKey(k => k + 1);
  };

  if (preferencesLoading) {
    return <div className="min-h-screen bg-background" />;
  }

  return (
    <>
      <AppShell
        onCalendarChange={handleCalendarChange}
        use24HourFormat={use24HourFormat}
        onTimeFormatToggle={() => setUse24HourFormat(!use24HourFormat)}
        setUse24HourFormat={setUse24HourFormat}
        selectedCities={selectedCities}
        onAddCity={handleAddCity}
        getLocalizedCityName={getLocalizedCityName}
        onOpenCalendarPreferences={handleOpenCalendarPreferences}
        onOpenTimezoneGroups={handleOpenTimezoneGroups}
        onToggleHelp={handleToggleHelp}
        onLoadGroup={handleLoadGroup}
      >
        <AuthWrapper>
          <App
            calendarRefreshKey={calendarRefreshKey}
            selectedTimezones={selectedTimezones}
            onRemoveCity={handleRemoveCity}
            onReorderCities={handleReorderCities}
            onOpenCalendarPreferences={handleOpenCalendarPreferences}
            use24HourFormat={use24HourFormat}
          />
        </AuthWrapper>
      </AppShell>

      <CalendarPreferences
        isOpen={showCalendarPreferences}
        onClose={() => setShowCalendarPreferences(false)}
        onCalendarChange={handleCalendarChange}
      />

      {showCalendarConnect && (
        <div className="calendar-connect-overlay">
          <div className="calendar-connect-modal">
            <div className="modal-header">
              <h3>{t('calendar.googleCalendar')}</h3>
              <button className="close-button" onClick={() => setShowCalendarConnect(false)}>×</button>
            </div>
            <div className="modal-content">
              <GoogleCalendarConnect
                onConnectionChange={(connected) => {
                  if (connected) {
                    setShowCalendarConnect(false);
                    handleCalendarChange();
                    setShowCalendarPreferences(true);
                  }
                }}
                onSettingsClick={() => {
                  setShowCalendarConnect(false);
                  setShowCalendarPreferences(true);
                }}
              />
            </div>
          </div>
        </div>
      )}

      <React.Suspense fallback={null}>
        <TimezoneGroups
          isOpen={showTimezoneGroups}
          onClose={() => setShowTimezoneGroups(false)}
          selectedTimezones={selectedTimezones}
          onLoadGroup={handleLoadGroup}
          onSaveCurrentGroup={handleSaveCurrentGroup}
          onOpenCommandPalette={() => {
            setShowTimezoneGroups(false);
            setTimeout(() => {
              const ev = new KeyboardEvent('keydown', { key: 'k', metaKey: true, ctrlKey: true, bubbles: true });
              document.dispatchEvent(ev);
            }, 100);
          }}
        />
      </React.Suspense>

      <CalendarSignInModal isOpen={showCalendarSignIn} onClose={() => setShowCalendarSignIn(false)} />

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} />
    </>
  );
}
